import React, { useState, useEffect } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Modal, ModalBody, ModalFooter, Button } from './ui';
import { KPIService } from '../services/kpiService';
import './CorrectionsReasonModal.css';

/**
 * Modal for requiring reason and comment when sending a project to Corrections.
 * Only Matt and Adam can trigger this flow.
 * Logs the correction to kpi_corrections_log for KPI tracking.
 *
 * Reasons are fetched from database and grouped into:
 * - "Counts Against Builder" (affects KPI)
 * - "Does Not Count" (doesn't affect KPI)
 */
const CorrectionsReasonModal = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
  projectName,
  jobNumber,
  modelBuilderId,
  modelBuilderName,
  userEmail,
  userId,
}) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [correctionReasons, setCorrectionReasons] = useState({
    counts_against: [],
    does_not_count: []
  });
  const [loadingReasons, setLoadingReasons] = useState(true);

  // Fetch correction reasons from database
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        setLoadingReasons(true);
        const reasons = await KPIService.getCorrectionReasons();
        setCorrectionReasons(reasons);
      } catch (err) {
        console.error('Error fetching correction reasons:', err);
      } finally {
        setLoadingReasons(false);
      }
    };

    if (isOpen) {
      fetchReasons();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedReasons([]);
      setComment('');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  // Toggle a reason in the selected list
  const toggleReason = (reason) => {
    setSelectedReasons(prev =>
      prev.includes(reason)
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
    setError(null);
  };

  // Check if any selected reason counts against the builder
  const countsAgainstBuilder = selectedReasons.some(reason =>
    correctionReasons.counts_against.includes(reason)
  );

  const handleSubmit = async () => {
    if (selectedReasons.length === 0) {
      setError('Please select at least one reason for the correction');
      return;
    }

    if (!comment.trim()) {
      setError('Please provide details about the correction');
      return;
    }

    if (!modelBuilderId) {
      setError('No model builder assigned to this project. Please assign one first.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Log the correction to the database
      await KPIService.logCorrection({
        projectId,
        modelBuilderId,
        reason: selectedReasons[0], // Primary reason for legacy/display
        selectedReasons,
        countsAgainstBuilder,
        comment: comment.trim(),
        loggedByEmail: userEmail,
        loggedByUserId: userId,
        jobNumber,
        projectName,
      });

      // Call the parent submit handler
      await onSubmit({ reason: selectedReasons[0], selectedReasons, countsAgainstBuilder, comment: comment.trim() });
    } catch (err) {
      console.error('Error logging correction:', err);
      setError('Failed to log correction. Please try again.');
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal trackAs="CorrectionsReasonModal" isOpen={isOpen} onClose={onClose} size="md" zIndex={10000}>
      {/* Header */}
      <div className="corrections-reason-header">
        <div className="corrections-reason-header-content">
          <div className="corrections-reason-icon">
            <AlertTriangle size={28} color="white" />
          </div>

          <div>
            <h1 className="corrections-reason-title">
              Send to Corrections
            </h1>
            <p className="corrections-reason-subtitle">
              {jobNumber} - {projectName}
            </p>
          </div>
        </div>

        <button onClick={onClose} className="corrections-reason-close">
          <X size={20} />
        </button>
      </div>

      <ModalBody>
        <div className="corrections-reason-body">
          {/* Model Builder Info */}
          <div className="corrections-reason-builder-info">
            <span className="corrections-reason-builder-label">Model Builder:</span>
            <span className="corrections-reason-builder-name">
              {modelBuilderName || 'Not assigned'}
            </span>
          </div>

          {!modelBuilderId && (
            <div className="corrections-reason-warning">
              No model builder is assigned to this project.
              Please assign a model builder before sending to Corrections.
            </div>
          )}

          {/* Reason Checkboxes */}
          <div className="corrections-reason-field">
            <label className="corrections-reason-label">
              Reason(s) for Correction <span className="required">*</span>
            </label>

            {loadingReasons ? (
              <div className="corrections-reason-loading">Loading reasons...</div>
            ) : (
              <div className="corrections-reason-groups">
                {/* Counts Against Builder Group */}
                <div className="corrections-reason-group corrections-reason-group--warning">
                  <div className="corrections-reason-group-header">
                    <span className="corrections-reason-group-title">Counts Against Builder</span>
                    <span className="corrections-reason-group-hint">Affects first-pass approval rate</span>
                  </div>
                  <div className="corrections-reason-checkboxes">
                    {correctionReasons.counts_against.map((r) => (
                      <label key={r} className="corrections-reason-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedReasons.includes(r)}
                          onChange={() => toggleReason(r)}
                          disabled={!modelBuilderId}
                        />
                        <span>{r}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Does Not Count Group */}
                <div className="corrections-reason-group corrections-reason-group--neutral">
                  <div className="corrections-reason-group-header">
                    <span className="corrections-reason-group-title">Does Not Count Against Builder</span>
                    <span className="corrections-reason-group-hint">No KPI impact</span>
                  </div>
                  <div className="corrections-reason-checkboxes">
                    {correctionReasons.does_not_count.map((r) => (
                      <label key={r} className="corrections-reason-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedReasons.includes(r)}
                          onChange={() => toggleReason(r)}
                          disabled={!modelBuilderId}
                        />
                        <span>{r}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* KPI Impact Indicator */}
            {selectedReasons.length > 0 && (
              <div className={`corrections-reason-impact ${countsAgainstBuilder ? 'corrections-reason-impact--warning' : 'corrections-reason-impact--neutral'}`}>
                {countsAgainstBuilder
                  ? 'This correction will count against the builder\'s first-pass approval rate.'
                  : 'This correction will NOT affect the builder\'s KPI.'}
              </div>
            )}
          </div>

          {/* Comment Textarea */}
          <div className="corrections-reason-field">
            <label className="corrections-reason-label">
              Details <span className="required">*</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                setError(null);
              }}
              placeholder="Describe the specific issues that need to be corrected..."
              className="corrections-reason-textarea"
              rows={5}
              disabled={!modelBuilderId}
            />
            <p className="corrections-reason-hint">
              This will be logged for KPI tracking and sent as a private DM to the model builder.
            </p>
          </div>

          {error && (
            <div className="corrections-reason-error">
              {error}
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={selectedReasons.length === 0 || !comment.trim() || !modelBuilderId || submitting}
        >
          {submitting ? 'Submitting...' : 'Send to Corrections'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CorrectionsReasonModal;
